import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  Download,
  Ellipsis,
  Eye,
  EyeOff,
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileText,
  FileVideo,
  Trash2,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { FileDto, FileKind } from '@cw/shared';
import { Badge } from '@/design-system/components/badge';
import { Button } from '@/design-system/components/button';
import { Dropzone, UploadList } from '@/design-system/components/dropzone';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/design-system/components/menu';
import { Segmented } from '@/design-system/components/segmented';
import { Skeleton } from '@/design-system/components/skeleton';
import { EmptyState, ErrorState } from '@/design-system/components/states';
import { relativeTime } from '@/features/activity/activity-text';
import { api } from '@/lib/api';
import { handleFormError } from '@/lib/forms';
import { useCan, useMe } from '@/lib/session';
import { formatBytes, useUploadQueue } from '@/lib/upload';

const ICON: Partial<Record<FileKind, typeof File>> = {
  image: FileImage,
  gif: FileImage,
  video: FileVideo,
  audio: FileAudio,
  pdf: FileText,
  document: FileText,
  archive: FileArchive,
};

type Target = {
  purpose: 'client_file' | 'project_file' | 'task_attachment' | 'request_attachment';
  clientId?: string;
  projectId?: string;
  taskId?: string;
  requestId?: string;
};

/**
 * Lista + envio de arquivos de um cliente ou projeto.
 * A biblioteca completa (pastas, versões, previews) chega na Fase 7.
 */
export function FilesPanel({
  target,
  files,
  invalidate,
  canUpload,
}: {
  target: Target;
  files: UseQueryResult<FileDto[], Error>;
  invalidate: readonly (readonly unknown[])[];
  canUpload: boolean;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');
  const keys = JSON.stringify(invalidate);
  const onDone = useCallback(() => {
    for (const k of JSON.parse(keys) as unknown[][]) qc.invalidateQueries({ queryKey: k });
  }, [qc, keys]);
  const queue = useUploadQueue({ ...target, visibility }, onDone);
  const visibleUploads = queue.items.filter((i) => i.status !== 'done');

  return (
    <div className="flex flex-col gap-5">
      {canUpload && (
        <div className="flex flex-col gap-3">
          <Dropzone onFiles={queue.add} />
          <div className="flex flex-wrap items-center gap-3 text-[13px] text-fg-muted">
            <span>Novos arquivos ficam visíveis para</span>
            <Segmented
              label="Visibilidade dos novos arquivos"
              value={visibility}
              onChange={setVisibility}
              options={[
                { value: 'internal', label: 'Só a equipe' },
                { value: 'client', label: 'Equipe e cliente' },
              ]}
            />
          </div>
          <UploadList
            items={visibleUploads}
            onCancel={queue.cancel}
            onRetry={queue.retry}
            onDismiss={queue.dismiss}
          />
        </div>
      )}

      {files.isPending ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : files.isError ? (
        <ErrorState error={files.error} onRetry={() => files.refetch()} />
      ) : files.data.length === 0 ? (
        <EmptyState
          icon={<File />}
          title="Nenhum arquivo ainda"
          description={
            canUpload
              ? 'Briefings, contratos, referências e entregas do cliente ficam aqui.'
              : 'Os arquivos compartilhados com você aparecem aqui.'
          }
        />
      ) : (
        <ul className="divide-y divide-line rounded-panel shadow-[inset_0_0_0_1px_var(--color-line)]">
          {files.data.map((f) => (
            <FileRow key={f.id} file={f} onChanged={onDone} showVisibility={!me.role.startsWith('client_')} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FileRow({
  file: f,
  onChanged: invalidate,
  showVisibility,
}: {
  file: FileDto;
  onChanged: () => void;
  showVisibility: boolean;
}) {
  const canUpload = useCan('files:upload');
  const canDelete = useCan('files:delete');
  const Icon = ICON[f.kind] ?? File;
  const toggle = useMutation({
    mutationFn: () =>
      api.patch(`/files/${f.id}`, { visibility: f.visibility === 'client' ? 'internal' : 'client' }),
    onSuccess: () => {
      invalidate();
      toast.success(
        f.visibility === 'client' ? 'Agora só a equipe vê este arquivo' : 'O cliente agora vê este arquivo',
      );
    },
    onError: (e) => handleFormError(e),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/files/${f.id}`),
    onSuccess: () => {
      invalidate();
      toast.success('Arquivo removido');
    },
    onError: (e) => handleFormError(e),
  });

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      {f.kind === 'image' || f.kind === 'gif' ? (
        <img
          src={f.url}
          alt=""
          loading="lazy"
          className="size-10 shrink-0 rounded-[8px] bg-raised object-cover"
        />
      ) : (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[8px] bg-raised text-fg-subtle">
          <Icon className="size-5" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <a
          href={f.url}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-[13.5px] font-medium hover:underline"
        >
          {f.name}
        </a>
        <p className="text-[12px] text-fg-subtle">
          {formatBytes(f.sizeBytes)}
          <span className="ml-2">
            {f.uploadedBy ? `${f.uploadedBy.split(' ')[0]}, ` : ''}
            {relativeTime(f.createdAt)}
          </span>
        </p>
      </div>
      {showVisibility && f.visibility === 'client' && <Badge tone="info">Cliente vê</Badge>}
      <Menu>
        <MenuTrigger asChild>
          <Button size="icon-sm" variant="ghost" aria-label={`Ações para ${f.name}`}>
            <Ellipsis />
          </Button>
        </MenuTrigger>
        <MenuContent align="end">
          <MenuItem icon={<Download />} onSelect={() => window.open(`${f.url}?download=1`, '_blank')}>
            Baixar
          </MenuItem>
          {canUpload && showVisibility && (
            <MenuItem
              icon={f.visibility === 'client' ? <EyeOff /> : <Eye />}
              onSelect={() => toggle.mutate()}
            >
              {f.visibility === 'client' ? 'Esconder do cliente' : 'Mostrar para o cliente'}
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem icon={<Trash2 />} danger onSelect={() => remove.mutate()}>
              Remover
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
    </li>
  );
}
